import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { enterDemand } from "../repo";

type EntryRedirectOptions = {
  timeoutMs?: number;
};

const DEFAULT_ENTRY_TIMEOUT_MS = 12_000;

/** 需求行程入口：幂等进入当前行程后替换为编辑器地址。 */
export function useItineraryEntryRedirect(
  demandId: string,
  options: EntryRedirectOptions = {},
) {
  const navigate = useNavigate();
  const location = useLocation();
  const startedDemandIdRef = useRef("");
  const activeRef = useRef(true);
  const requestGenerationRef = useRef(0);
  const [entryError, setEntryError] = useState<unknown>(null);
  const mutation = useMutation({ mutationFn: () => enterDemand(demandId) });
  const timeoutMs = options.timeoutMs ?? DEFAULT_ENTRY_TIMEOUT_MS;
  const run = useCallback(async () => {
    const targetDemandId = demandId;
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;
    setEntryError(null);
    try {
      const result = await withTimeout(mutation.mutateAsync(), timeoutMs);
      if (
        !activeRef.current
        || requestGenerationRef.current !== requestGeneration
        || startedDemandIdRef.current !== targetDemandId
      ) return;
      const params = new URLSearchParams();
      if (new URLSearchParams(location.search).get("embed") === "1") params.set("embed", "1");
      if (result.editor.persisted === false) params.set("draftDemandId", targetDemandId);
      const search = params.size ? `?${params}` : "";
      navigate(`/itineraries/${encodeURIComponent(result.editor.payload.id)}/edit${search}`, {
        replace: true,
      });
    } catch (error) {
      if (
        activeRef.current
        && requestGenerationRef.current === requestGeneration
        && startedDemandIdRef.current === targetDemandId
      ) setEntryError(error);
    }
  }, [demandId, location.search, mutation.mutateAsync, navigate, timeoutMs]);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      startedDemandIdRef.current = "";
      requestGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!demandId || startedDemandIdRef.current === demandId) return;
    startedDemandIdRef.current = demandId;
    void run();
  }, [demandId, run]);

  return {
    loading: mutation.isPending && entryError === null,
    error: entryError ?? mutation.error,
    retry: run,
  };
}

/** 避免入口请求异常挂起时把用户永久留在加载页。 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = globalThis.setTimeout(
      () => reject(new Error("itinerary-entry-timeout")),
      timeoutMs,
    );
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) globalThis.clearTimeout(timer);
  }
}
